/**
 * Creating a module inside a programme.
 *
 * @module components/programmes/CreateModuleForm
 */

import React, { useId } from "react";

import {
  Alert,
  Button,
  Combobox,
  EmptyState,
  FieldSet,
  FormField,
  MutedText,
  PageHeader,
  Panel,
  TextField,
} from "@/components/ui";

export interface ProgrammeOption {
  id: string;
  title: string;
  programmeCode?: string;
}

export interface CreateModuleFormProps {
  /** Only programmes the account may write to: the rest cannot receive one. */
  programmes: readonly ProgrammeOption[];
  programmeId: string;
  onProgrammeChange: (programmeId: string) => void;
  title: string;
  onTitleChange: (title: string) => void;
  code: string;
  onCodeChange: (code: string) => void;
  /** Held as typed, so the field can be emptied and retyped. */
  credits: string;
  onCreditsChange: (credits: string) => void;
  submitting: boolean;
  error?: string;
  onSubmit: () => void;
  onCancel: () => void;
  /** Shown when the account leads a programme, to staff it after creating. */
  teamAction?: React.ReactNode;
}

/**
 * @example
 * ```tsx
 * <CreateModuleForm programmes={writable} programmeId={id} … />
 * ```
 */
export function CreateModuleForm({
  programmes,
  programmeId,
  onProgrammeChange,
  title,
  onTitleChange,
  code,
  onCodeChange,
  credits,
  onCreditsChange,
  submitting,
  error = undefined,
  onSubmit,
  onCancel,
  teamAction = undefined,
}: CreateModuleFormProps): React.ReactElement {
  const missingId = useId();
  const missing: string[] = [];
  const creditsValue = Number(credits);
  if (programmes.length === 0) {
    // The fields are replaced by the empty state in this branch, so naming
    // them points at a form that is not on the screen. What is missing is
    // somewhere to put the module.
    missing.push("a programme you can add a module to");
  } else {
    if (!programmeId) {
      missing.push("a programme");
    }
    if (title.trim().length === 0) {
      missing.push("a module title");
    }
    if (credits.trim().length === 0 || !Number.isFinite(creditsValue) || creditsValue < 0) {
      missing.push("a credit value of zero or more");
    }
  }
  // Derived from `missing`, not stated a second time. The two used to
  // disagree: clearing Credits emptied `missing`, which unmounted the whole
  // "Still needed" block and dropped `aria-describedby`, while the submit
  // stayed disabled with nothing anywhere saying why.
  const canSubmit = missing.length === 0;

  return (
    <form
      className="flex max-w-3xl flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        // The submit is `aria-disabled`, not `disabled`, so it no longer
        // suppresses implicit submission, and the button's guard sees only a
        // press. `submitting` too: overlapping runs are allowed, so a second
        // submit creates a second thing under a fresh id.
        if (!canSubmit || submitting) {
          return;
        }
        onSubmit();
      }}
      data-testid="create-module-form"
    >
      <PageHeader
        title="Create a new module"
        description="It is created inside a programme and assigned to you."
        actions={teamAction}
      />

      {error ? <Alert tone="warning">{error}</Alert> : null}

      {programmes.length === 0 ? (
        <EmptyState
          icon="lock-key"
          title="No programme you can add a module to"
          description="You create a module inside a programme you can edit. Ask for a lead or builder responsibility on one."
          data-testid="create-module-no-programmes"
        />
      ) : (
        <Panel>
          <FieldSet legend="Module details" columns={2}>
            <FormField
              label="Programme"
              htmlFor="new-module-programme"
              helpText="The programme to create the module in."
              required
            >
              <Combobox
                id="new-module-programme"
                value={programmeId}
                onChange={onProgrammeChange}
                options={programmes.map((programme) => ({
                  value: programme.id,
                  label: programme.title,
                  hint: programme.programmeCode,
                }))}
                placeholder="Choose a programme"
                emptyMessage="No programmes match"
                required
                data-testid="new-module-programme"
              />
            </FormField>
            <FormField
              label="Module title"
              htmlFor="new-module-title"
              helpText="A learner-facing title that describes the module's scope."
              required
            >
              <TextField
                id="new-module-title"
                value={title}
                onChange={onTitleChange}
                placeholder="e.g. Applied Databases"
                required
              />
            </FormField>
            <FormField
              label="Module code"
              htmlFor="new-module-code"
              helpText="Optional now; add the institutional code once it is known."
            >
              <TextField
                id="new-module-code"
                value={code}
                onChange={onCodeChange}
                placeholder="e.g. COMP501"
              />
            </FormField>
            <FormField
              label="Credits"
              htmlFor="new-module-credits"
              helpText="Used for workload and progression calculations."
              required
            >
              <TextField
                id="new-module-credits"
                type="number"
                min={0}
                step={1}
                value={credits}
                onChange={onCreditsChange}
                suffix="credits"
                required
              />
            </FormField>
          </FieldSet>
        </Panel>
      )}

      <div className="flex flex-wrap items-center justify-end gap-3">
        {/* As on the programme form: a disabled submit is out of the tab order
            and runs no validation of its own, so the reason has to be on it. */}
        {missing.length > 0 && (
          <div id={missingId} aria-live="polite" className="mr-auto">
            <MutedText size="caption" data-testid="create-module-missing">
              Still needed: {missing.join(", ")}.
            </MutedText>
          </div>
        )}
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          icon="plus-circle"
          loading={submitting}
          loadingLabel="Creating the module"
          aria-describedby={missing.length > 0 ? missingId : undefined}
          aria-disabled={!canSubmit || undefined}
        >
          Create module and continue
        </Button>
      </div>
    </form>
  );
}
